<template>
  <div class="histogram-wrapper">
    <div class="section-description mb-4">Our model currently predicts a
      <span class="highlight-value" :style="`color: ${winningParty.color};`">
        {{ winningParty.perc }}
      </span>
      <template> chance of a {{ winningParty.party }} majority.</template>
      The model has moved
      <span v-for="(key, index) in Object.keys(change)" :key="index">
        <span
          v-if="Math.abs(change[key].dem.toFixed(2)) === Math.abs(change[key].rep.toFixed(2))"
          :class="['highlight-value', getParty(key)]">
          {{ change[key][getParty(key)] | movement }} {{ getParty(key).charAt(0).toUpperCase() }}
        </span>
        <template v-else>
          <span class="highlight-value gop">
            {{ change[key].rep | movement }} R
          </span>
          </template>
        <span> {{ getMovementText(key) }} </span>
      </span>
    </div>
    <histogram
      :key="`${chamber}-histogram-${currentDate}`"
      :chamber="chamber"
      :majority="majority"
      :totalSeats="totalSeats"
      :gopProb="probabilityOfGopMajority"
      :movement="modelMovement"
      :simulations="currentModel.simulations"
      @on-mousemove="info = $event">
    </histogram>
    <div class="chart-interaction-info">
      <div v-if="info && info.value > 0">
        <b>{{ info.value | commas }}</b>
        of our simulations predict a {{ getOutcome(info).value }}</div>
      <div style="opacity: 0.5;" v-else>
        <template v-if="mobile">Tap on </template>
        <template v-else>Hover over </template>
        the chart for more
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import { Histogram } from '@/components/charts'

const components = {
  Histogram
}

const props = {
  chamber: {
    type: String,
    required: true
  },
  models: {
    type: Array,
    required: true
  },
  currentModel: {
    type: Object,
    required: true
  }
}

const computed = {
  ...mapGetters({
    dates: 'aggregateModels/dates',
    mobile: 'ux/mobile'
  }),
  currentDate () {
    return this.$store.getters['aggregateModels/currentDate'](this.chamber)
  },
  change () {
    return this.currentModel.change
  },
  probabilityOfGopMajority () {
    return (this.currentModel.probabilityOfGopMajority * 100).toFixed(1) + '%'
  },
  totalSeats () {
    if (this.chamber === 'house') {
      return 435
    } else if (this.chamber.includes('senate')) {
      return 100
    } else if (this.chamber === 'president') {
      return 538
    }
  },
  majority () {
    if (this.chamber === 'house') {
      return 218
    } else if (this.chamber.includes('senate')) {
      return 51
    }
  },
  sortedSimulations () {
    let simulations = [...this.currentModel.simulations].filter(s => s.value > 0)
    return simulations.sort((a, b) => b.value - a.value)
  },
  movementText () {
    return {
      value: `+${this.modelMovement.diff}% ${this.modelMovement.party}`,
      color: this.modelMovement.diff === '0' ? 'var(--text-color)' : this.modelMovement.party === 'R' ? 'var(--gop)' : 'var(--dem)'
    }
  },
  winningParty () {
    if (this.currentModel.probabilityOfGopMajority > 0.5) {
      return {
        perc: (this.currentModel.probabilityOfGopMajority * 100).toFixed(1) + '%',
        party: 'GOP',
        color: 'var(--gop)'
      }
    } else {
      return {
        perc: (this.currentModel.probabilityOfDemMajority * 100).toFixed(1) + '%',
        party: 'Democratic',
        color: 'var(--dem)'
      }
    }
  }
}

const methods = {
  getMovementText (key) {
    if (key === 'oneDay') {
      return 'over the last day,'
    } else if (key === 'oneWeek') {
      return 'over the last week, and'
    } else {
      return 'over the last month.'
    }
  },
  getParty (key) {
    return this.change[key].dem > this.change[key].rep ? 'dem' : 'rep'
  },
  getOutcome (sim) {
    let template = {}
    const split = `${sim.seats}R / ${(this.majority * 2) - sim.seats}D`
    if (sim.seats >= this.majority) {
      template = {
        value: 'GOP majority',
        party: 'gop',
        extra:  `- ${split} split`
      }
    } else {
      template = {
        value: 'Dem majority',
        party: 'dem',
        extra:  `- ${split} split`
      }
    }
    return template
  },
  getModelMovement (models, key) {
    const index = this.dates.indexOf(this.currentDate)
    const current = models.find(model => model.date === this.currentDate)
    const prev = models.find(model => model.date === this.dates[index - 1])
    if (prev && index > 0) {
      let diff = Math.round((current[key] - prev[key]) * 10000) / 100
      diff = diff.toString()
      let party = {
        diff: null,
        party: null
      }

      if (diff.includes('-')) {
        party = 'Democrats'
        diff = diff.replace('-', '')
      } else {
        party = 'Republican'
      }
      this.modelMovement.diff = diff
      this.modelMovement.party = party.charAt(0)
    } else {
      this.modelMovement.diff = null
    }
  }
}

export default {
  components,
  props,
  computed,
  methods,
  data () {
    return {
      info: null,
      modelMovement: {
        diff: null,
        party: null
      }
    }
  },
  mounted () {
    this.getModelMovement(this.models, 'probabilityOfGopMajority')
  }
}
</script>

<style>
.chart-interaction-info {
  margin-bottom: 20px;
  text-align: center;
  font-size: 14px;
}

.sims {
  margin-top: 20px;
  text-align: center;
}

.sim-wrapper {
  line-height: 18px;
  font-size: 14px;
  font-weight: 400;
  display: flex;
}

.sim-headers {
  font-size: 13px;
  opacity: 0.5;
  display: flex;
  margin-bottom: 5px;
}

.sim-col {
  display: inline-block;
  width: 100px;
  text-align: end;
}

.outcome-col {
  text-align: start;
  display: inline-block;
  width: calc(100% - 120px);
  border-left: 1px solid #bdbdbd;
  padding-left: 10px;
  margin-left: 10px;
}

.outcome-col span {
  opacity: 0.8;
}

.outcome-value {
  font-weight: 700;
  margin-right: 5px;
  opacity: 1 !important;
}

.outcome-extra {
  font-weight: 400;
}
</style>
